import React, { useState, useEffect } from 'react';
import { Order } from '../types';
import { ordersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button, Input, Card, Modal } from './ui';
import { printReceipt } from '../utils/printReceipt';
// Lazy loading imports removed for now

// Order tipini genişlet
interface ExtendedOrder extends Order {
  receipt_requested?: boolean;
}

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<ExtendedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderFilter, setOrderFilter] = useState<'all' | 'mutfak' | 'hazır' | 'ödeme_bekliyor' | 'kart_ödeme_bekliyor' | 'ödendi' | 'tamamlandı' | 'iptal'>('all');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'nakit' | 'kart' | 'qr'>('all');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [tempDateInput, setTempDateInput] = useState<string>(new Date().toISOString().split('T')[0]); // Geçici tarih inputu
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Modal states
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ExtendedOrder | null>(null);
  const [completedOrder, setCompletedOrder] = useState<ExtendedOrder | null>(null);
  const [paymentType, setPaymentType] = useState<'nakit' | 'kart'>('nakit');
  const [processing, setProcessing] = useState(false);

  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
    const interval = setInterval(() => {
      loadOrders(false); // Sadece sipariş listesini yenile, loading gösterme
    }, 15000); // Auto refresh every 15 seconds
    return () => clearInterval(interval);
  }, [selectedDate]);

  // selectedDate değiştiğinde tempDateInput'u da güncelle (başka yerden değiştiyse)
  useEffect(() => {
    setTempDateInput(selectedDate);
  }, [selectedDate]);

  // Arama veya filtre değiştiğinde sayfayı sıfırla
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, orderFilter, paymentFilter]);

  const loadOrders = async (showLoading: boolean = true) => {
    try {
      // Scroll pozisyonunu kaydet
      const scrollPosition = window.scrollY;

      if (showLoading) {
        setLoading(true);
      }
      const data = await ordersAPI.getAll(selectedDate);
      // İptal edilen siparişleri filtreleme - iptal sekmesi için gerekli
      setOrders(data);
      if (showLoading) {
        setLoading(false);
      }

      // Scroll pozisyonunu geri yükle
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    } catch (error: any) {
      // console.error('❌ Sipariş yükleme hatası:', error);
      if (showLoading) {
        toast.error(`Siparişler yüklenemedi: ${error.response?.data?.error || error.message}`);
        setLoading(false);
      }
    }
  };

  // Helper functions
  const getOrderTotal = (order: ExtendedOrder | null | undefined) => {
    if (!order || !order.items) return 0;
    return order.items.reduce((total, item) => total + (item.quantity * item.unit_price), 0) || 0;
  };

  const getOrderTax = (order: ExtendedOrder | null | undefined) => {
    if (!order || !order.items) return 0;
    // Fiyata Dahil Mod (Belçika) - Vergi fiyatın içinden çıkarılır
    return order.items.reduce((total, item) => {
      const itemTotal = item.quantity * item.unit_price;
      const taxRate = item.product?.tax_rate || 0;
      const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
      return total + taxAmount;
    }, 0) || 0;
  };

  const getOrderGrandTotal = (order: ExtendedOrder | null | undefined) => {
    if (!order) return 0;
    // Fiyatlar zaten vergili - tekrar ekleme!
    const subtotal = getOrderTotal(order);
    const giftCodeDiscount = (order as any)?.gift_code?.discount_amount || 0;
    return Math.max(0, subtotal - giftCodeDiscount);
  };

  // Order actions
  const handleViewDetails = (order: ExtendedOrder) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handlePaymentClick = (order: ExtendedOrder) => {
    setSelectedOrder(order);
    setPaymentType('nakit');
    setShowPaymentModal(true);
  };

  // POS bekleme durumu
  const [waitingForPos, setWaitingForPos] = useState(false);
  const [posPollingInterval, setPosPollingInterval] = useState<any | null>(null);
  const [isWaitingForPosResponse, setIsWaitingForPosResponse] = useState(false);

  // POS ödeme durumu kontrolü - webhook tabanlı
  // POS ödeme durumu kontrolü - webhook tabanlı + polling fallback
  // Polling tekrar eklendi - Backend'den DB durumu sorgulanır (SumUp API değil)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (waitingForPos && selectedOrder) {
      // İlk kontrol hemen yap
      checkPaymentStatus(false);

      // Her 3 saniyede bir kontrol et
      interval = setInterval(() => {
        checkPaymentStatus(false);
      }, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [waitingForPos, selectedOrder]);

  const checkPaymentStatus = async (isManual = false) => {
    if (!selectedOrder) return;

    try {
      if (isManual) {
        toast.loading('Durum kontrol ediliyor...', { id: 'check-status' });
      }

      // Sadece ilgili siparişi getir
      const currentOrder = await ordersAPI.getById(selectedOrder.id);

      if (isManual) {
        toast.dismiss('check-status');
      }

      if (currentOrder) {
        console.log('🔍 Payment Status Check:', currentOrder.status);

        // Eğer ödeme tamamlandıysa
        if (currentOrder.status === 'ödendi') {
          setWaitingForPos(false);
          setCompletedOrder(currentOrder); // Fiş yazdırma modalı için
          setShowPaymentModal(false);
          setShowReceiptModal(true);
          toast.success('✅ POS ödemesi başarıyla alındı!');
          loadOrders(); // Listeyi yenile
        }
        else if (isManual) {
          if (currentOrder.status === 'kart_ödeme_bekliyor') {
            toast.success('Ödeme bekleniyor...');
          } else {
            toast.error(`Durum: ${currentOrder.status}`);
          }
        }
      }
    } catch (error) {
      if (isManual) {
        toast.dismiss('check-status');
        toast.error('Durum kontrol edilemedi');
      }
      console.error('Payment check error:', error);
    }
  };

  const handlePaymentProcess = async () => {
    if (!selectedOrder) return;

    // KART ödemesi için özel durum: Backend response'unu beklerken buton durumunu güncelle
    if (paymentType === 'kart') {
      console.log('💳 Kart ödemesi başlatılıyor (OrderManagement) - Backend response bekleniyor...');
      setIsWaitingForPosResponse(true);
      setProcessing(true);
    } else {
      setProcessing(true);
    }

    try {
      console.log('📤 Backend\'e istek gönderiliyor (OrderManagement)...', {
        orderId: selectedOrder.id,
        paymentType
      });

      const response = await ordersAPI.pay(selectedOrder.id, paymentType);

      console.log('📥 Backend Response alındı (OrderManagement):', response);
      console.log('📥 Response.checkoutId:', response?.checkoutId);
      console.log('📥 Response.success:', response?.success);

      // KART ÖDEME: SumUp aktifse POS bekleme moduna geç, değilse normal akışa devam et
      if (paymentType === 'kart') {
        // Backend'den response geldi, kontrol et
        const hasCheckoutId = response && response.checkoutId;
        const hasSuccess = response && response.success;
        const hasMessage = response && response.message; // SumUp pasifken döner

        console.log('🔍 Kart ödeme kontrolü (OrderManagement):', {
          hasCheckoutId,
          hasSuccess,
          hasMessage,
          checkoutId: response?.checkoutId,
          success: response?.success,
          message: response?.message
        });

        // SumUp AKTİF: checkoutId veya success varsa POS bekleme moduna geç
        if (hasCheckoutId || hasSuccess) {
          // Backend'den response geldi, artık bekleme moduna geç
          console.log('✅ Backend response başarılı (OrderManagement), bekleme moduna geçiliyor...');
          setIsWaitingForPosResponse(false);
          setProcessing(false);

          toast.success('💳 POS cihazına gönderildi. Lütfen kartı okutun...', {
            duration: 5000,
            id: 'card-payment-start'
          });

          // Modal'ı KAPATMA, bekleme moduna geç
          setWaitingForPos(true);
          // Listeyi yenile (status: kart_ödeme_bekliyor olmalı)
          loadOrders();
          return;
        }
        // SumUp PASİF: message varsa ödeme tamamlandı demektir, normal akışa devam et
        else if (hasMessage) {
          console.log('✅ Kart ödemesi tamamlandı (SumUp pasif):', response.message);
          // Normal akışa devam et (aşağıdaki NAKİT akışı kullanılacak)
        }
        // Hiçbiri yoksa hata
        else {
          console.error('❌ Backend\'den beklenmeyen response (OrderManagement):', response);
          toast.error('Ödeme işlenemedi. Lütfen tekrar deneyin.');
          setIsWaitingForPosResponse(false);
          setProcessing(false);
          return;
        }
      }

      // NAKİT ödeme için mevcut akış
      const updatedOrder = {
        ...selectedOrder,
        status: 'ödendi' as const,
        payment_type: paymentType,
        paid_at: new Date().toISOString()
      };

      setCompletedOrder(updatedOrder);
      setShowPaymentModal(false);
      setShowReceiptModal(true);

      toast.success(`✅ Ödeme başarıyla alındı! (${paymentType.toUpperCase()})`);
      loadOrders();
    } catch (error: any) {
      console.error('❌ Ödeme hatası:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Ödeme işlemi başarısız';

      // SumUp hatası ise özel mesaj göster
      if (errorMessage.includes('SumUp') || errorMessage.includes('sumup')) {
        toast.error(`💳 Kart ödeme hatası: ${errorMessage}. Lütfen nakit ödeme seçeneğini deneyin.`);
      } else {
        toast.error(`Ödeme işlenirken hata oluştu: ${errorMessage}`);
      }

      // Hata durumunda siparişleri yenile
      loadOrders();
    } finally {
      setIsWaitingForPosResponse(false);
      if (paymentType !== 'kart') {
        setProcessing(false);
      }
    }
  };

  // Modal kapatıldığında polling'i durdur
  const handleClosePaymentModal = () => {
    setWaitingForPos(false);
    if (posPollingInterval) clearInterval(posPollingInterval);
    setShowPaymentModal(false);
  };

  const handleCompleteOrder = async (orderId: number) => {
    try {
      await ordersAPI.complete(orderId);
      toast.success('✅ Sipariş tamamlandı!');
      loadOrders();
    } catch (error: any) {
      // console.error('❌ Sipariş tamamlama hatası:', error);
      toast.error(`Sipariş tamamlanırken hata oluştu: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCloseOrder = async (orderId: number) => {
    try {
      await ordersAPI.close(orderId);
      toast.success('✅ Sipariş kapatıldı!');
      loadOrders();
    } catch (error: any) {
      // console.error('❌ Sipariş kapatma hatası:', error);
      toast.error(`Sipariş kapatılırken hata oluştu: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCancelOrder = async (orderId: number) => {
    // İptal edilecek siparişi bul
    const orderToCancel = orders.find(order => order.id === orderId);

    let confirmMessage = 'Bu siparişi iptal etmek istediğinizden emin misiniz?';

    // Eğer sipariş ödenmişse iade bilgisi ekle
    if (orderToCancel?.payment_type) {
      const paymentTypeName = orderToCancel.payment_type === 'nakit' ? 'Nakit' : orderToCancel.payment_type === 'qr' ? 'QR' : 'Kart';
      confirmMessage = `Bu sipariş ödenmiş durumda (${paymentTypeName}). İptal edildiğinde iade yapılacaktır. İptal etmek istediğinizden emin misiniz?`;
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      await ordersAPI.cancel(orderId);

      // İptal sonrası mesaj
      if (orderToCancel?.payment_type) {
        const paymentTypeName = orderToCancel.payment_type === 'nakit' ? 'Nakit' : orderToCancel.payment_type === 'qr' ? 'QR' : 'Kart';
        toast.success(`✅ Sipariş iptal edildi! İade işlemi başlatıldı (${paymentTypeName}).`);
      } else {
        toast.success('✅ Sipariş iptal edildi!');
      }

      loadOrders();
    } catch (error: any) {
      // console.error('❌ Sipariş iptal hatası:', error);
      toast.error(`Sipariş iptal edilirken hata oluştu: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleMarkReady = async (orderId: number) => {
    try {
      await ordersAPI.markReady(orderId);
      toast.success('✅ Sipariş hazır durumuna getirildi!');
      loadOrders();
    } catch (error: any) {
      console.error('❌ Sipariş hazır durumuna getirme hatası:', error);
      toast.error(`Sipariş hazır durumuna getirilemedi: ${error.response?.data?.error || error.message}`);
    }
  };

  const printReceiptFromModal = async () => {
    if (completedOrder) {
      printReceipt(completedOrder);


    }
  };

  const finishPayment = () => {
    setShowReceiptModal(false);
    setCompletedOrder(null);
  };

  // Filtrelenmiş siparişler (tüm siparişlerde arama yapılır)
  const filteredOrders = orders.filter(order => {
    // Sadece geçersiz siparişleri filtrele (total_amount < 0 veya null), ödül siparişleri (0 TL) görünsün
    if (order.total_amount < 0) return false;
    if (orderFilter !== 'all' && order.status !== orderFilter) return false;

    // Ödeme türü filtresi - sadece ödendi durumunda aktif
    if (orderFilter === 'ödendi' && paymentFilter !== 'all' && order.payment_type !== paymentFilter) {
      return false;
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();

      // Masa numarası formatı: <=18 ise T{numara}, >18 ise C{numara-18}
      let masaFormati = '';
      if (order.table?.table_number) {
        if (order.table.table_number <= 18) {
          masaFormati = `t${order.table.table_number}`;
        } else {
          masaFormati = `c${order.table.table_number - 18}`;
        }
      }

      return (
        order.id.toString().includes(searchLower) ||
        (order.table?.table_number && order.table.table_number.toString().includes(searchLower)) ||
        (masaFormati && masaFormati.includes(searchLower)) ||
        (order.user?.username && order.user.username.toLowerCase().includes(searchLower))
      );
    }

    return true;
  });

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Sayfa değiştiğinde yukarı kaydır
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cafe-brown mx-auto mb-4"></div>
          <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-700'}`}>Siparişler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>📋 Siparişler</h1>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Hoş geldiniz, {user?.username}</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="success" size="md" onClick={() => navigate('/')}>
                🚀 Hızlı Siparişe Dön
              </Button>
              {(user?.role === 'admin' || user?.role === 'garson') && (
                <Button variant="primary" size="md" onClick={() => navigate('/admin')}>
                  ⚙️ Admin Panel
                </Button>
              )}
              <Button
                variant="warning"
                size="md"
                onClick={async () => {
                  try {
                    const result = await ordersAPI.bulkReady();
                    toast.success(result.message);
                    await loadOrders();
                  } catch (error: any) {
                    toast.error(error.response?.data?.error || 'Toplu güncelleme başarısız');
                  }
                }}
                disabled={processing}
              >
                ✅ Tümünü Hazır Yap
              </Button>
              <Button variant="info" size="md" onClick={loadOrders}>
                🔄 Yenile
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Date Picker */}
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              📅 Tarih
            </label>
            <Input
              type="date"
              value={tempDateInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTempDateInput(e.target.value); // Sadece geçici state'i güncelle
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                // Input'tan çıkıldığında tarihi güncelle ve yükle
                if (e.target.value !== selectedDate) {
                  setSelectedDate(e.target.value);
                  setCurrentPage(1); // Sayfayı sıfırla
                }
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                // Enter'a basıldığında tarihi güncelle ve yükle
                if (e.key === 'Enter') {
                  const target = e.target as HTMLInputElement;
                  if (target.value !== selectedDate) {
                    setSelectedDate(target.value);
                    setCurrentPage(1); // Sayfayı sıfırla
                  }
                  target.blur(); // Input'tan çık
                }
              }}
              className="w-auto"
            />
          </div>

          {/* Search */}
          <div className="relative max-w-md mb-4">
            <Input
              type="text"
              placeholder="Sipariş ara (ID, masa, garson)..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filters */}
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              📋 Durum
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'Tümü', color: 'gray' },
                { key: 'mutfak', label: 'Mutfakta', color: 'yellow' },
                { key: 'hazır', label: 'Hazır', color: 'orange' },
                { key: 'kart_ödeme_bekliyor', label: '💳 Kart Bekliyor', color: 'purple' },
                { key: 'ödeme_bekliyor', label: 'Ödeme Bekliyor', color: 'blue' },
                { key: 'ödendi', label: 'Ödendi', color: 'green' },
                { key: 'tamamlandı', label: 'Tamamlandı', color: 'gray' },
                { key: 'iptal', label: 'İptal', color: 'red' }
              ].map(filter => (
                <Button
                  key={filter.key}
                  variant={orderFilter === filter.key ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => {
                    setOrderFilter(filter.key as any);
                    // Ödendi dışında bir duruma geçildiğinde payment filter'ı sıfırla
                    if (filter.key !== 'ödendi') {
                      setPaymentFilter('all');
                    }
                  }}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Payment Type Tabs - Only for paid orders */}
          {orderFilter === 'ödendi' && (
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                💳 Ödeme Türü
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'all', label: 'Tümü', icon: '💰' },
                  { key: 'nakit', label: 'Nakit', icon: '💵' },
                  { key: 'kart', label: 'Kart', icon: '💳' },
                  { key: 'qr', label: 'QR', icon: '📱' }
                ].map(filter => (
                  <Button
                    key={filter.key}
                    variant={paymentFilter === filter.key ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => setPaymentFilter(filter.key as any)}
                  >
                    {filter.icon} {filter.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Orders List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card>
          <div className="p-6">
            <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Siparişler ({filteredOrders.length} toplam • {paginatedOrders.length} bu sayfada)
            </h2>

            {filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📋</div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Sipariş bulunamadı
                </h3>
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                  {orderFilter === 'all' ? 'Henüz sipariş yok.' : `${orderFilter} durumunda sipariş yok.`}
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {paginatedOrders.map((order) => (
                    <div key={order.id} className={`p-4 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              Sipariş #{order.id}
                            </h3>
                            <div className="flex gap-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'mutfak' ? 'bg-yellow-100 text-yellow-800' :
                                order.status === 'hazır' ? 'bg-orange-100 text-orange-800' :
                                  order.status === 'kart_ödeme_bekliyor' ? 'bg-purple-100 text-purple-800' :
                                    order.status === 'ödeme_bekliyor' ? 'bg-blue-100 text-blue-800' :
                                      order.status === 'ödendi' ? 'bg-green-100 text-green-800' :
                                        order.status === 'tamamlandı' ? 'bg-gray-100 text-gray-800' :
                                          'bg-red-100 text-red-800'
                                }`}>
                                {order.status === 'mutfak' ? '🍽️ Mutfakta' :
                                  order.status === 'hazır' ? '🍛 Hazır' :
                                    order.status === 'kart_ödeme_bekliyor' ? '💳 POS Ödeme Bekleniyor' :
                                      order.status === 'ödeme_bekliyor' ? '💳 Ödeme Bekliyor' :
                                        order.status === 'ödendi' ? '✅ Ödendi' :
                                          order.status === 'tamamlandı' ? '🏁 Tamamlandı' :
                                            '❌ İptal'}
                              </span>
                              {/* Hızlı siparişler için ödendi etiketi */}
                              {order.table_id === 0 && order.payment_type && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  💰 Ödendi
                                </span>
                              )}
                              {/* Hediye kodu etiketi */}
                              {(order as any)?.gift_code && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  🎁 Hediye Kodu: {(order as any).gift_code.code}
                                </span>
                              )}
                              {/* Fiş isteme etiketi - Sadece Nakit Ödemelerde */}
                              {order.receipt_requested && order.payment_type === 'nakit' && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  🧾 Fiş İstedi
                                </span>
                              )}
                            </div>
                            {order.payment_type && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.payment_type === 'nakit'
                                ? 'bg-green-100 text-green-800'
                                : order.payment_type === 'qr'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                                }`}>
                                {order.payment_type === 'nakit' ? '💵 Nakit' : order.payment_type === 'qr' ? '📱 QR' : '💳 Kart'}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {!order.table ? '🚀 Hızlı Sipariş' : `🍽️ Masa ${order.table.table_number <= 18 ? `T${order.table.table_number}` : `C${order.table.table_number - 18}`}`} •
                            👤 {order.user?.username} •
                            🕐 {new Date(order.created_at).toLocaleString('tr-TR', { timeZone: 'Europe/Brussels' })}
                          </p>
                          <p className={`text-lg font-bold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {(order as any)?.gift_code && (order as any).gift_code.discount_amount > 0 ? (
                              <>
                                <span className="line-through text-gray-400 mr-2">
                                  {formatPrice(order.total_amount)}
                                </span>
                                <span className="text-green-600">
                                  💰 {formatPrice(order.total_amount - (order as any).gift_code.discount_amount)}
                                </span>
                                <span className="ml-2 text-sm text-green-600">
                                  (İndirim: -{formatPrice((order as any).gift_code.discount_amount)})
                                </span>
                              </>
                            ) : (
                              <>💰 {formatPrice(order.total_amount)}</>
                            )}
                            {order.total_amount === 0 && (
                              <span className="ml-2 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                                🎁 Ödül Siparişi
                              </span>
                            )}
                          </p>
                          {/* İptal edilmiş ödenmiş siparişler için iade bilgisi */}
                          {order.status === 'iptal' && order.payment_type && (
                            <p className={`text-xs mt-1 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                              💡 Ödeme iade edilecek ({order.payment_type === 'nakit' ? 'Nakit' : order.payment_type === 'qr' ? 'QR' : 'Kart'})
                            </p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col space-y-2">
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleViewDetails(order)}
                          >
                            👁️ Detay
                          </Button>

                          {order.status === 'mutfak' && (
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleMarkReady(order.id)}
                            >
                              🍛 Hazır
                            </Button>
                          )}

                          {(order.status === 'hazır' && order.table_id !== 0) && (
                            user?.can_process_payments || user?.role === 'admin' ? (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handlePaymentClick(order)}
                              >
                                💳 Ödeme Al
                              </Button>
                            ) : (
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled
                                title={''}
                              >
                                🚫 Ödeme İzni Yok
                              </Button>
                            )
                          )}

                          {(order.status === 'ödendi') || (order.status === 'hazır' && order.table_id === 0) && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleCompleteOrder(order.id)}
                            >
                              ✅ Tamamla
                            </Button>
                          )}

                          {(order.status === 'mutfak' || order.status === 'hazır' || order.status === 'ödeme_bekliyor' || order.status === 'ödendi' || order.status === 'tamamlandı') && (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleCancelOrder(order.id)}
                            >
                              ❌ İptal
                            </Button>
                          )}



                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => printReceipt(order)}
                          >
                            🖨️ Fiş
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-center space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage((prev: number) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      ← Önceki
                    </Button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 10) {
                          pageNum = i + 1;
                        } else if (currentPage <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 4) {
                          pageNum = totalPages - 9 + i;
                        } else {
                          pageNum = currentPage - 5 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className="min-w-[40px]"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage((prev: number) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Sonraki →
                    </Button>
                  </div>
                )}

                {/* Sayfa bilgisi */}
                <div className="mt-4 text-center">
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Sayfa {currentPage} / {totalPages} • Toplam {filteredOrders.length} sipariş
                  </p>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Auto Refresh Info */}
      <div className="text-center py-4">
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          🔄 Sipariş listesi otomatik olarak 15 saniyede bir yenilenir
        </p>
      </div>

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <Modal
          isOpen={showOrderDetails}
          onClose={() => setShowOrderDetails(false)}
          title={`📋 Sipariş Detayları #${selectedOrder.id}`}
          size="lg"
        >
          <div className="space-y-6">
            {/* Order Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Sipariş No</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>#{selectedOrder.id}</p>
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Masa</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {!selectedOrder.table ? '🚀 Hızlı Sipariş' : `🍽️ Masa ${selectedOrder.table.table_number <= 18 ? `T${selectedOrder.table.table_number}` : `C${selectedOrder.table.table_number - 18}`}`}
                </p>
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Durum</p>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedOrder.status === 'mutfak' ? 'bg-yellow-100 text-yellow-800' :
                    selectedOrder.status === 'hazır' ? 'bg-orange-100 text-orange-800' :
                      selectedOrder.status === 'ödeme_bekliyor' ? 'bg-blue-100 text-blue-800' :
                        selectedOrder.status === 'ödendi' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                    }`}>
                    {selectedOrder.status === 'mutfak' ? '🍽️ Mutfakta' :
                      selectedOrder.status === 'hazır' ? '🍛 Hazır' :
                        selectedOrder.status === 'ödeme_bekliyor' ? '💳 Ödeme Bekliyor' :
                          selectedOrder.status === 'ödendi' ? '✅ Ödendi' :
                            selectedOrder.status}
                  </span>
                  {/* Hızlı siparişler için ödendi etiketi */}
                  {selectedOrder.table_id === 0 && selectedOrder.payment_type && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      💰 Ödendi
                    </span>
                  )}
                  {/* Fiş isteme etiketi - Sadece Nakit Ödemelerde */}
                  {selectedOrder.receipt_requested && selectedOrder.payment_type === 'nakit' && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      🧾 Fiş İstedi
                    </span>
                  )}
                  {selectedOrder.payment_type && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedOrder.payment_type === 'nakit'
                      ? 'bg-green-100 text-green-800'
                      : selectedOrder.payment_type === 'qr'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                      }`}>
                      {selectedOrder.payment_type === 'nakit' ? '💵 Nakit' : selectedOrder.payment_type === 'qr' ? '📱 QR' : '💳 Kart'}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Tarih</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {new Date(selectedOrder.created_at).toLocaleString('tr-TR')}
                </p>
              </div>
            </div>

            {/* Order Items */}
            <div>
              <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Sipariş İçeriği</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedOrder?.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <div className="flex-1">
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        {item.product?.name}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {item.quantity} x {formatPrice(item.unit_price)}
                      </div>
                      {item.notes && (
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          📝 Not: {item.notes}
                        </div>
                      )}
                    </div>
                    <div className={`text-right font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {formatPrice(item.quantity * item.unit_price)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Total */}
              <div className={`border-t pt-3 mt-3 space-y-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Ara Toplam:</span>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                    {formatPrice(getOrderTotal(selectedOrder))}
                  </span>
                </div>
                {(selectedOrder as any)?.gift_code && (selectedOrder as any).gift_code.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-700 dark:text-green-400">
                    <span>🎁 Hediye Kodu ({(selectedOrder as any).gift_code.code}):</span>
                    <span>-{formatPrice((selectedOrder as any).gift_code.discount_amount || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>KDV:</span>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                    {formatPrice(getOrderTax(selectedOrder))}
                  </span>
                </div>
                <div className={`flex justify-between font-bold text-lg border-t pt-2 ${isDark ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}>
                  <span>TOPLAM:</span>
                  <span className="text-cafe-brown dark:text-cafe-light">
                    {formatPrice(getOrderGrandTotal(selectedOrder))}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => printReceipt(selectedOrder)}
              >
                🖨️ Fiş Yazdır
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={() => setShowOrderDetails(false)}
              >
                Kapat
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Payment Modal */}
      {selectedOrder && (
        <Modal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          title="💳 Ödeme Al"
          size="md"
        >
          {waitingForPos ? (
            // POS BEKLEME EKRANI
            <div className={`text-center space-y-6 py-4`}>
              <div className="animate-pulse">
                <div className="text-6xl mb-4">💳</div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  POS Ödemesi Bekleniyor...
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Lütfen POS cihazından işlemi tamamlayın.<br />
                  Sipariş durumu otomatik olarak güncellenecektir.
                </p>
              </div>

              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>

              <div className="pt-4 space-y-3">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => checkPaymentStatus(true)}
                >
                  🔄 Durumu Kontrol Et
                </Button>

                <Button
                  variant="danger"
                  size="md"
                  onClick={handleClosePaymentModal}
                  fullWidth
                >
                  ⚠️ İşlemi İptal Et / Kapat
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Sipariş Özeti */}
              <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-750' : 'bg-gray-50'}`}>
                <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {!selectedOrder?.table ? 'Hızlı Sipariş' : `Masa ${selectedOrder.table.table_number <= 18 ? `T${selectedOrder.table.table_number}` : `C${selectedOrder.table.table_number - 18}`}`} - Sipariş Özeti
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedOrder?.items?.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <div className="flex-1">
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                          {item.product?.name}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {item.quantity} x {formatPrice(item.unit_price)}
                        </div>
                      </div>
                      <div className={`text-right font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        {formatPrice(item.quantity * item.unit_price)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`border-t pt-3 mt-3 space-y-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                  <div className="flex justify-between font-bold text-lg">
                    <span className={isDark ? 'text-white' : 'text-gray-800'}>TOPLAM:</span>
                    <span className="text-cafe-brown dark:text-cafe-light">
                      {formatPrice(getOrderGrandTotal(selectedOrder))}
                    </span>
                  </div>
                </div>
              </div>

              {/* Ödeme Türü Seçimi */}
              <div>
                <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  Ödeme Türü Seçin
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPaymentType('nakit')}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'nakit'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                  >
                    <div className="text-2xl mb-2">💵</div>
                    <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Nakit</div>
                  </button>
                  <button
                    onClick={() => setPaymentType('kart')}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'kart'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                  >
                    <div className="text-2xl mb-2">💳</div>
                    <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Kart</div>
                  </button>
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={() => setShowPaymentModal(false)}
                >
                  İptal
                </Button>
                <Button
                  variant="success"
                  size="lg"
                  fullWidth
                  onClick={handlePaymentProcess}
                  disabled={processing || isWaitingForPosResponse}
                >
                  {isWaitingForPosResponse ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      POS Ödeme bekleniyor...
                    </div>
                  ) : processing ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      İşleniyor...
                    </div>
                  ) : (
                    '✅ Ödemeyi Tamamla'
                  )}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Fiş Modalı */}
      <Modal
        isOpen={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        title={''}
        size="md"
      >
        <div className="text-center space-y-4">
          <div className="text-green-600">
            <div className="text-4xl mb-3">✅</div>
            <h3 className="text-xl font-bold mb-2">Ödeme Başarıyla Alındı!</h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Sipariş #{completedOrder?.id} - {!completedOrder?.table ? 'Hızlı Sipariş' : `Masa ${completedOrder.table.table_number <= 18 ? `T${completedOrder.table.table_number}` : `C${completedOrder.table.table_number - 18}`}`}<br />
              Toplam: {formatPrice(completedOrder ? getOrderGrandTotal(completedOrder) : 0)}<br />
              Ödeme Türü: {(completedOrder?.payment_type)?.toUpperCase()}
              {(completedOrder as any)?.gift_code && (
                <>
                  <br />🎁 Hediye Kodu Kullanıldı: {(completedOrder as any).gift_code.code}
                  <br />İndirim: {formatPrice((completedOrder as any).gift_code.discount_amount || 0)}
                </>
              )}
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={printReceiptFromModal}
            >
              🖨️ Fiş Yazdır
            </Button>
            <Button
              variant="success"
              size="lg"
              fullWidth
              onClick={finishPayment}
            >
              ✅ Tamamla
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderManagement;
